/**
 * Action History Manager
 * Tracks and manages the action history log based on actual game events
 * No guessing, no calculations - just logs what actually happened
 */

import i18n from './i18n';

/**
 * Detect new actions by comparing previous and current game state
 * Returns array of new action entries
 */
export function detectNewActions(prevState, currState, userId, existingActionHistory = []) {
  const actions = [];
  const currentTurn = currState.turnNumber || currState.turn;
  const allPlayerIds = currState.players || Object.keys(currState.state);
  
  if (!prevState || !prevState.state) {
    return actions; // No previous state to compare
  }
  
  // ✅ FIX: Track strategems that were already detected on strategem-initiated
  // In the two-phase system, strategems are detected immediately when strategem-initiated is received,
  // so we should skip them here to avoid duplicates
  const alreadyDetectedStrategemInstanceIds = new Set(
    existingActionHistory
      .filter(action => action.type === 'strategem-requested' && action.order?.instanceId)
      .map(action => action.order.instanceId)
  );
  
  // 0. Detect strategems played FIRST (before unit deployments)
  // This ensures strategems appear before units they create/transform
  const strategemActions = [];
  const strategemCreatedUnitInstanceIds = new Set(); // Track units created by strategems
  
  allPlayerIds.forEach(playerId => {
    const prevPlayer = prevState.state?.[playerId];
    const currPlayer = currState.state?.[playerId];
    if (!prevPlayer || !currPlayer) return;
    
    const prevHand = prevPlayer.hand || [];
    const currHand = currPlayer.hand || [];
    const prevCompletedOrders = prevPlayer.completedOrders || [];
    const currCompletedOrders = currPlayer.completedOrders || [];
    
    // Find strategems that were in hand but are now gone
    // ✅ FIX: Only treat orders with type 'Strategem' as strategems. Do NOT use description/name
    // containing "strategem" - that falsely matches Unit 109 (Signal Breaker, "All Strategems cost 2 more.")
    // and causes "Signal Breaker has been played" to appear in action history when that unit is destroyed.
    prevHand.forEach(prevOrder => {
      if (!prevOrder) return;
      const orderType = (prevOrder.type || '').toLowerCase();
      const isStrategem = orderType === 'strategem';
      if (!isStrategem) return;
      
      // Check if this strategem is no longer in hand
      const stillInHand = currHand.some(
        o => o && o.instanceId === prevOrder.instanceId
      );
      
      if (!stillInHand) {
        // Strategem was removed from hand - check if it's now in completedOrders (by type only)
        const nowInCompleted = currCompletedOrders.some(
          o => o && o.instanceId === prevOrder.instanceId && (o.type || '').toLowerCase() === 'strategem'
        );
        
        // Check if it wasn't already in completedOrders before
        const wasAlreadyCompleted = prevCompletedOrders.some(
          o => o && o.instanceId === prevOrder.instanceId && (o.type || '').toLowerCase() === 'strategem'
        );
        
        // If it moved from hand to completedOrders, it was played
        // ✅ FIX: Skip if this strategem was already detected on strategem-initiated (two-phase system)
        if (nowInCompleted && !wasAlreadyCompleted && !alreadyDetectedStrategemInstanceIds.has(prevOrder.instanceId)) {
          // Try to detect targets by checking state changes
          const targets = [];
          
          // 1. Check for units killed by this strategem (in completedOrders with destructionType === 'strategem')
          // ✅ FIX: Also check units that disappeared from battlefield (might be in completedOrders in next state update)
          allPlayerIds.forEach(targetPlayerId => {
            const prevTargetPlayer = prevState.state?.[targetPlayerId];
            const currTargetPlayer = currState.state?.[targetPlayerId];
            if (!prevTargetPlayer || !currTargetPlayer) return;
            
            // Check completedOrders for newly destroyed units
            const prevCompleted = prevTargetPlayer.completedOrders || [];
            const currCompleted = currTargetPlayer.completedOrders || [];
            
            currCompleted.forEach(destroyedUnit => {
              if (!destroyedUnit || !destroyedUnit.instanceId) return;
              
              // Check if this unit was destroyed this turn by a strategem
              const wasDestroyedThisTurn = !prevCompleted.some(
                o => o && o.instanceId === destroyedUnit.instanceId
              );
              
              if (wasDestroyedThisTurn && 
                  (destroyedUnit.destructionType === 'strategem' || 
                   (destroyedUnit.destroyedBy && destroyedUnit.destroyedBy.startsWith('Strategem ')))) {
                // Extract strategem ID from destroyedBy (format: "Strategem 55 (Vein Sever)" or "Strategem 226 (Embalm)")
                const strategemMatch = destroyedUnit.destroyedBy?.match(/Strategem (\d+)/);
                if (strategemMatch && parseInt(strategemMatch[1]) === prevOrder.id) {
                  // Check if we already added this target
                  const alreadyAdded = targets.some(t => t.instanceId === destroyedUnit.instanceId);
                  if (!alreadyAdded) {
                    targets.push({
                      id: destroyedUnit.id,
                      name: destroyedUnit.name,
                      instanceId: destroyedUnit.instanceId,
                      died: true
                    });
                  }
                }
              }
            });
            
            // ✅ FIX: Also check for units that disappeared from battlefield (might be destroyed but not yet in completedOrders)
            // This catches cases where the unit was destroyed but the detection runs before it's added to completedOrders
            ['front', 'flank'].forEach(zone => {
              if (!prevTargetPlayer.playedOrders?.[zone]) return;
              
              prevTargetPlayer.playedOrders[zone].forEach((prevUnit, idx) => {
                if (!prevUnit || !prevUnit.instanceId) return;
                
                // Check if unit disappeared from battlefield
                const currUnit = currTargetPlayer.playedOrders?.[zone]?.[idx];
                const unitDisappeared = !currUnit || currUnit.instanceId !== prevUnit.instanceId;
                
                if (unitDisappeared) {
                  // Check if this unit is now in completedOrders with strategem destruction info
                  const destroyedUnit = currCompleted.find(
                    o => o && o.instanceId === prevUnit.instanceId &&
                    (o.destructionType === 'strategem' || 
                     (o.destroyedBy && o.destroyedBy.startsWith('Strategem ')))
                  );
                  
                  if (destroyedUnit) {
                    // Extract strategem ID from destroyedBy
                    const strategemMatch = destroyedUnit.destroyedBy?.match(/Strategem (\d+)/);
                    if (strategemMatch && parseInt(strategemMatch[1]) === prevOrder.id) {
                      // Check if we already added this target
                      const alreadyAdded = targets.some(t => t.instanceId === prevUnit.instanceId);
                      if (!alreadyAdded) {
                        targets.push({
                          id: prevUnit.id,
                          name: prevUnit.name,
                          instanceId: prevUnit.instanceId,
                          died: true
                        });
                      }
                    }
                  }
                }
              });
            });
          });
          
          // 2. Check for units enhanced by this strategem (enhancedBy with sourceType === 'strategem')
          allPlayerIds.forEach(targetPlayerId => {
            const targetPlayer = currState.state?.[targetPlayerId];
            if (!targetPlayer) return;
            
            ['front', 'flank'].forEach(zone => {
              if (!targetPlayer.playedOrders?.[zone]) return;
              
              targetPlayer.playedOrders[zone].forEach(unit => {
                if (!unit || !unit.instanceId) return;
                
                const enhancedBy = Array.isArray(unit.enhancedBy) ? unit.enhancedBy : (unit.enhancedBy ? [unit.enhancedBy] : []);
                enhancedBy.forEach(enhancement => {
                  if (enhancement && 
                      enhancement.sourceType === 'strategem' && 
                      enhancement.instanceId === prevOrder.instanceId) {
                    // ✅ FIX: Check currentTurn OR currentTurn - 1 to catch bot actions
                    // Bot actions happen during their turn, but when the turn ends, turnNumber increments
                    const enhancementTurn = enhancement.turn;
                    const isRecentTurn = enhancementTurn === currentTurn || enhancementTurn === currentTurn - 1;
                    if (!isRecentTurn) return;
                    // Check if this enhancement wasn't in previous state
                    const prevPlayer = prevState.state?.[targetPlayerId];
                    const prevUnit = prevPlayer?.playedOrders?.[zone]?.find(
                      u => u && u.instanceId === unit.instanceId
                    );
                    const prevEnhancedBy = prevUnit?.enhancedBy ? 
                      (Array.isArray(prevUnit.enhancedBy) ? prevUnit.enhancedBy : [prevUnit.enhancedBy]) : [];
                    const wasAlreadyEnhanced = prevEnhancedBy.some(e => 
                      e && e.instanceId === enhancement.instanceId && e.turn === enhancement.turn
                    );
                    
                    if (!wasAlreadyEnhanced) {
                      // Check if we already added this target
                      const alreadyAdded = targets.some(t => t.instanceId === unit.instanceId);
                      if (!alreadyAdded) {
                        targets.push({
                          id: unit.id,
                          name: unit.name,
                          instanceId: unit.instanceId
                        });
                      }
                    }
                  }
                });
              });
            });
          });
          
          // 3. Check for units that took damage from this strategem (health decreased or damageTaken increased)
          // This handles strategems like Damage Pulse, Guided Missile (317) that deal damage but don't kill or enhance
          // ✅ FIX: Exclude strategems that only destroy friendly units (like Kamikaze ID 314)
          // Kamikaze destroys a friendly unit and deals damage to the general, not to other units
          // ✅ FIX: Exclude single-target destroy-only strategems (e.g. Tactical Breach 181). They only destroy one unit
          // (set in step 1 via destroyedBy); step 3 has no way to know which damage came from this strategem, so it
          // incorrectly adds every unit that took damage this turn (combat, other effects), producing "Tactical Breach on [3 units]".
          const isKamikaze = prevOrder.id === 314;
          const isSingleTargetDestroyOnly = prevOrder.id === 181; // Tactical Breach - destroy one damaged enemy, no damage to others
          if (!isKamikaze && !isSingleTargetDestroyOnly) {
            allPlayerIds.forEach(targetPlayerId => {
              const prevTargetPlayer = prevState.state?.[targetPlayerId];
              const currTargetPlayer = currState.state?.[targetPlayerId];
              if (!prevTargetPlayer || !currTargetPlayer) return;
              
              ['front', 'flank'].forEach(zone => {
                // ✅ FIX: Check both current battlefield AND completedOrders for units that took damage
                // Units that took damage and then died should still be detected
                
                // Check units still on battlefield
                if (currTargetPlayer.playedOrders?.[zone]) {
                  currTargetPlayer.playedOrders[zone].forEach((currUnit, idx) => {
                    if (!currUnit || !currUnit.instanceId) return;
                    
                    // Find the same unit in previous state
                    const prevUnit = prevTargetPlayer.playedOrders?.[zone]?.[idx];
                    if (!prevUnit || prevUnit.instanceId !== currUnit.instanceId) return;
                    
                    // Check if unit took damage this turn
                    const prevHealth = prevUnit.health || prevUnit.maxHealth || 0;
                    const currHealth = currUnit.health || currUnit.maxHealth || 0;
                    const prevDamageTaken = prevUnit.damageTaken || 0;
                    const currDamageTaken = currUnit.damageTaken || 0;
                    
                    const healthDecreased = currHealth < prevHealth;
                    const damageTakenIncreased = currDamageTaken > prevDamageTaken;
                    
                    // If unit took damage and wasn't killed or enhanced by this strategem, it might be the target
                    if ((healthDecreased || damageTakenIncreased) && currUnit.health > 0) {
                      // Check if we already added this target (might have been added as killed or enhanced)
                      const alreadyAdded = targets.some(t => t.instanceId === currUnit.instanceId);
                      if (!alreadyAdded) {
                        targets.push({
                          id: currUnit.id,
                          name: currUnit.name,
                          instanceId: currUnit.instanceId,
                          damaged: true
                        });
                      }
                    }
                  });
                }
                
                // ✅ FIX: Also check completedOrders for units that took damage before dying
                // This catches cases like Damage Pulse where a unit takes damage and dies in the same turn
                const prevCompleted = prevTargetPlayer.completedOrders || [];
                const currCompleted = currTargetPlayer.completedOrders || [];
                
                currCompleted.forEach(destroyedUnit => {
                  if (!destroyedUnit || !destroyedUnit.instanceId) return;
                  
                  // Check if this unit was on battlefield in previous state
                  const prevUnit = prevTargetPlayer.playedOrders?.[zone]?.find(
                    u => u && u.instanceId === destroyedUnit.instanceId
                  );
                  
                  if (prevUnit) {
                    // Unit was on battlefield, check if it took damage before dying
                    const prevHealth = prevUnit.health || prevUnit.maxHealth || 0;
                    const finalHealth = destroyedUnit.finalHealth !== undefined ? destroyedUnit.finalHealth : (destroyedUnit.health || 0);
                    const prevDamageTaken = prevUnit.damageTaken || 0;
                    
                    const healthDecreased = finalHealth < prevHealth;
                    
                    // If unit took damage (even if it died), it might be a target
                    // But only if it wasn't already added as a killed target
                    if (healthDecreased) {
                      const alreadyAdded = targets.some(t => t.instanceId === destroyedUnit.instanceId);
                      if (!alreadyAdded) {
                        // Only add if this looks like a damage-dealing strategem (not a direct destroy)
                        // Direct destroy strategems (like Vein Sever) should be caught in step 1
                        // Damage strategems will have health > 0 before destruction or damageTaken > 0
                        targets.push({
                          id: destroyedUnit.id,
                          name: destroyedUnit.name,
                          instanceId: destroyedUnit.instanceId,
                          damaged: true,
                          died: true // Also mark as died since it's in completedOrders
                        });
                      }
                    }
                  }
                });
              });
            });
          }
          
          // 4. Check for units transformed by strategem 226 (Embalm) - unit disappears and unit 259 appears in same position
          if (prevOrder.id === 226) {
            allPlayerIds.forEach(targetPlayerId => {
              const prevTargetPlayer = prevState.state?.[targetPlayerId];
              const currTargetPlayer = currState.state?.[targetPlayerId];
              if (!prevTargetPlayer || !currTargetPlayer) return;
              
              ['front', 'flank'].forEach(zone => {
                if (!prevTargetPlayer.playedOrders?.[zone] || !currTargetPlayer.playedOrders?.[zone]) return;
                
                prevTargetPlayer.playedOrders[zone].forEach((prevUnit, index) => {
                  if (!prevUnit || !prevUnit.instanceId) return;
                  
                  // Check if this unit disappeared
                  const currUnit = currTargetPlayer.playedOrders[zone][index];
                  const unitDisappeared = !currUnit || currUnit.instanceId !== prevUnit.instanceId;
                  
                  if (unitDisappeared) {
                    // Check if unit 259 (Mummy) appeared in the same position
                    if (currUnit && currUnit.id === 259) {
                      // This unit was transformed - add the original unit as target
                      // Also mark the mummy as created by this strategem so it doesn't get logged as a separate deployment
                      strategemCreatedUnitInstanceIds.add(currUnit.instanceId);
                      
                      const alreadyAdded = targets.some(t => t.instanceId === prevUnit.instanceId);
                      if (!alreadyAdded) {
                        targets.push({
                          id: prevUnit.id,
                          name: prevUnit.name,
                          instanceId: prevUnit.instanceId
                        });
                      }
                    }
                  }
                });
              });
            });
          }
          
          // 5. Check for units decommissioned by strategem 111 (Kinetron Blast) - unit disappears from battlefield and appears in hand
          // Kinetron Blast removes a unit from the battlefield and adds a fresh copy (with new instanceId) to the owner's hand
          if (prevOrder.id === 111) {
            allPlayerIds.forEach(targetPlayerId => {
              const prevTargetPlayer = prevState.state?.[targetPlayerId];
              const currTargetPlayer = currState.state?.[targetPlayerId];
              if (!prevTargetPlayer || !currTargetPlayer) return;
              
              ['front', 'flank'].forEach(zone => {
                if (!prevTargetPlayer.playedOrders?.[zone]) return;
                
                prevTargetPlayer.playedOrders[zone].forEach((prevUnit, index) => {
                  if (!prevUnit || !prevUnit.instanceId) return;
                  
                  // Check if this unit disappeared from battlefield
                  const currUnitOnBattlefield = currTargetPlayer.playedOrders?.[zone]?.[index];
                  const unitDisappeared = !currUnitOnBattlefield || currUnitOnBattlefield.instanceId !== prevUnit.instanceId;
                  
                  if (unitDisappeared) {
                    // Check if a unit with the same ID was added to this player's hand (decommissioned)
                    // Note: The decommissioned unit gets a new instanceId, so we check by ID
                    // Also check if the unit wasn't in hand before (to avoid false positives)
                    const wasInHandBefore = prevTargetPlayer.hand?.some(handUnit => 
                      handUnit && handUnit.id === prevUnit.id
                    );
                    const wasAddedToHand = currTargetPlayer.hand?.some(handUnit => 
                      handUnit && handUnit.id === prevUnit.id
                    );
                    
                    // Unit was decommissioned if it disappeared from battlefield and a unit with same ID was added to hand
                    const wasDecommissioned = !wasInHandBefore && wasAddedToHand;
                    
                    if (wasDecommissioned) {
                      // This unit was decommissioned - add it as target
                      const alreadyAdded = targets.some(t => t.instanceId === prevUnit.instanceId);
                      if (!alreadyAdded) {
                        targets.push({
                          id: prevUnit.id,
                          name: prevUnit.name,
                          instanceId: prevUnit.instanceId
                        });
                      }
                    }
                  }
                });
              });
            });
          }
          
          // 6. Check for unit repositioned by strategem 186 (Phase Traverse) - same instanceId, different zone (front <-> flank)
          if (prevOrder.id === 186) {
            const prevPlayer = prevState.state?.[playerId];
            const currPlayer = currState.state?.[playerId];
            if (prevPlayer && currPlayer && prevPlayer.playedOrders && currPlayer.playedOrders) {
              ['front', 'flank'].forEach(fromZone => {
                const toZone = fromZone === 'front' ? 'flank' : 'front';
                if (!prevPlayer.playedOrders[fromZone]) return;
                prevPlayer.playedOrders[fromZone].forEach((prevUnit) => {
                  if (!prevUnit || !prevUnit.instanceId) return;
                  const stillInFromZone = currPlayer.playedOrders[fromZone]?.some(
                    u => u && u.instanceId === prevUnit.instanceId
                  );
                  if (stillInFromZone) return;
                  const nowInToZone = currPlayer.playedOrders[toZone]?.some(
                    u => u && u.instanceId === prevUnit.instanceId
                  );
                  if (!nowInToZone) return;
                  const alreadyAdded = targets.some(t => t.instanceId === prevUnit.instanceId);
                  if (!alreadyAdded) {
                    targets.push({
                      id: prevUnit.id,
                      name: prevUnit.name,
                      instanceId: prevUnit.instanceId,
                      repositioned: true,
                      fromZone,
                      toZone
                    });
                  }
                });
              });
            }
          }
          
          // Build message with target information
          let message = `${prevOrder.name} ${i18n.t('battleUI.actionHistory.hasBeenRequested', { defaultValue: 'was requested' })}`;
          if (targets.length > 0) {
            const phaseTraverseTarget = prevOrder.id === 186 && targets.length === 1 && targets[0].repositioned;
            if (phaseTraverseTarget) {
              const fromZone = targets[0].fromZone;
              const toZone = targets[0].toZone;
              const key = fromZone === 'front' ? 'battleUI.actionHistory.movedFromFrontToFlank' : 'battleUI.actionHistory.movedFromFlankToFront';
              message = i18n.t(key, { unitName: targets[0].name, defaultValue: fromZone === 'front' ? `Moved ${targets[0].name} from front to flank.` : `Moved ${targets[0].name} from flank to front.` });
            } else {
              message = `${prevOrder.name} ${i18n.t('battleUI.actionHistory.hasBeenRequestedOn', { defaultValue: 'was requested on' })}`;
            }
          }
          
          strategemActions.push({
            type: 'strategem-requested',
            playerId: playerId,
            timestamp: Date.now(),
            order: {
              id: prevOrder.id,
              name: prevOrder.name,
              instanceId: prevOrder.instanceId
            },
            targets: targets.length > 0 ? targets : undefined,
            target: targets.length === 1 ? targets[0] : undefined,
            message: message
          });
        }
      }
    });
  });
  
  // Add strategem actions first (before unit deployments)
  actions.push(...strategemActions);
  
  // 1. Detect unit deployments
  // ✅ GROUP DEPLOYMENTS: Collect deployments first, then group by deployedBy (for strategem 58)
  const deploymentGroups = new Map(); // Maps group key -> { source, targets[], playerId, zone }
  const individualDeployments = []; // Deployments without deployedBy (normal deployments)
  
  allPlayerIds.forEach(playerId => {
    const prevPlayer = prevState.state?.[playerId];
    const currPlayer = currState.state?.[playerId];
    
    // ✅ DEBUG: Log state structure for bot player
    if (playerId === 'bot' || String(playerId).includes('bot')) {
      console.log(`🤖 BOT STATE DEBUG:`, {
        playerId,
        hasPrevPlayer: !!prevPlayer,
        hasCurrPlayer: !!currPlayer,
        prevPlayerKeys: prevPlayer ? Object.keys(prevPlayer) : null,
        currPlayerKeys: currPlayer ? Object.keys(currPlayer) : null,
        prevHasPlayedOrders: !!prevPlayer?.playedOrders,
        currHasPlayedOrders: !!currPlayer?.playedOrders,
        prevFrontCount: prevPlayer?.playedOrders?.front?.filter(u => u).length || 0,
        currFrontCount: currPlayer?.playedOrders?.front?.filter(u => u).length || 0,
        prevFlankCount: prevPlayer?.playedOrders?.flank?.filter(u => u).length || 0,
        currFlankCount: currPlayer?.playedOrders?.flank?.filter(u => u).length || 0
      });
    }
    
    if (!prevPlayer || !currPlayer) return;
    
    ['front', 'flank'].forEach(zone => {
      if (!currPlayer.playedOrders?.[zone]) return;
      
      currPlayer.playedOrders[zone].forEach((currUnit, idx) => {
        if (!currUnit) return;
        
        // Check if this unit is new (not in previous state at this position)
        const prevUnit = prevPlayer.playedOrders?.[zone]?.[idx];
        const isNewUnitAtPosition = !prevUnit || prevUnit.instanceId !== currUnit.instanceId;
        
        // Also check if unit exists elsewhere in previous state (might have moved)
        // If it exists elsewhere, it's not a new deployment
        let unitExistsElsewhere = false;
        if (currUnit.instanceId && prevPlayer.playedOrders) {
          ['front', 'flank'].forEach(checkZone => {
            if (unitExistsElsewhere) return;
            if (prevPlayer.playedOrders[checkZone]) {
              const foundInPrev = prevPlayer.playedOrders[checkZone].some(
                u => u && u.instanceId === currUnit.instanceId
              );
              if (foundInPrev) {
                unitExistsElsewhere = true;
              }
            }
          });
        }
        
        // Unit is a new deployment if:
        // 1. It's new at this position AND
        // 2. It doesn't exist elsewhere in previous state
        // Note: We don't need to check completedOrders because if a unit is in playedOrders,
        // it can't be in completedOrders at the same time. If it was destroyed, it wouldn't
        // be in currPlayer.playedOrders to begin with.
        const isNewDeployment = isNewUnitAtPosition && !unitExistsElsewhere;
        
        // ✅ DEBUG: Log all deployment checks for unit 239 (Kek) to diagnose bot deployment issue
        if (currUnit.id === 239) {
          console.log(`🔍 DEPLOYMENT DETECTION DEBUG for Kek:`, {
            playerId,
            zone,
            index: idx,
            unitName: currUnit.name,
            instanceId: currUnit.instanceId,
            isNewUnitAtPosition,
            unitExistsElsewhere,
            isNewDeployment,
            prevUnit: prevUnit ? { id: prevUnit.id, name: prevUnit.name, instanceId: prevUnit.instanceId } : null,
            prevPlayerHasPlayedOrders: !!prevPlayer.playedOrders,
            currPlayerHasPlayedOrders: !!currPlayer.playedOrders,
            prevStateKeys: prevState ? Object.keys(prevState) : null,
            currStateKeys: currState ? Object.keys(currState) : null
          });
        }
        
        if (isNewDeployment) {
          // New unit deployed
          const zoneName = zone === 'front' ? 'Front' : 'Flank';
          
          // ✅ DEBUG: Log when deployment action is created
          if (currUnit.id === 239) {
            console.log(`✅ CREATING DEPLOYMENT ACTION for Kek`);
          }
          
          // Check if this unit was deployed by a strategem (e.g., strategem 58)
          // ✅ FIX: Check currentTurn OR currentTurn - 1 to catch bot actions
          if (currUnit.deployedBy && currUnit.deployedBy.type === 'strategem') {
            const deploymentTurn = currUnit.deployedBy.deploymentTurn;
            const isRecentTurn = deploymentTurn === currentTurn || deploymentTurn === currentTurn - 1;
            if (isRecentTurn) {
              // Group this deployment with others from the same strategem
              const groupKey = `strategem-${currUnit.deployedBy.instanceId || currUnit.deployedBy.id}`;
              
              if (!deploymentGroups.has(groupKey)) {
                // Find the strategem in completedOrders
                const strategem = currPlayer.completedOrders?.find(
                  o => o && o.instanceId === currUnit.deployedBy.instanceId && o.type === 'Strategem'
                ) || {
                  id: currUnit.deployedBy.id,
                  name: currUnit.deployedBy.name,
                  instanceId: currUnit.deployedBy.instanceId,
                  type: 'Strategem'
                };
                
                deploymentGroups.set(groupKey, {
                  source: strategem,
                  targets: [],
                  playerId: playerId,
                  zone: zone,
                  zoneName: zoneName
                });
              }
              
              // Add this unit to the group's targets
              deploymentGroups.get(groupKey).targets.push({
                id: currUnit.id,
                name: currUnit.name,
                instanceId: currUnit.instanceId
              });
            } else {
              // Not deployed by strategem in recent turn, treat as normal deployment
              const deploymentAction = {
                type: 'unit-deployed',
                playerId: playerId,
                timestamp: Date.now(),
                unit: {
                  id: currUnit.id,
                  name: currUnit.name,
                  instanceId: currUnit.instanceId
                },
                zone: zone,
                zoneName: zoneName,
                message: `${currUnit.name} ${i18n.t('battleUI.actionHistory.hasBeenDeployedIn', { zone: zoneName })}`
              };
              
              individualDeployments.push(deploymentAction);
            }
          } else {
            // Normal deployment (not grouped)
            const deploymentAction = {
              type: 'unit-deployed',
              playerId: playerId,
              timestamp: Date.now(),
              unit: {
                id: currUnit.id,
                name: currUnit.name,
                instanceId: currUnit.instanceId
              },
              zone: zone,
              zoneName: zoneName,
              message: `${currUnit.name} ${i18n.t('battleUI.actionHistory.hasBeenDeployedIn', { zone: zoneName })}`
            };
            
            individualDeployments.push(deploymentAction);
          }
        }
      });
    });
  });
  
  // ✅ GROUP DEPLOYMENTS: Convert grouped deployments into actions
  deploymentGroups.forEach((group) => {
    const targetCount = group.targets.length;
    const sourceName = group.source.name;
    
    // Create message based on number of targets (localized)
    let message;
    if (targetCount === 1) {
      message = i18n.t('battleUI.actionHistory.sourceDeployedUnit', {
        sourceName,
        unitName: group.targets[0].name,
        defaultValue: `${sourceName} deployed ${group.targets[0].name}`
      });
    } else {
      message = i18n.t('battleUI.actionHistory.sourceDeployedUnits', {
        sourceName,
        count: targetCount,
        defaultValue: `${sourceName} deployed ${targetCount} units`
      });
    }
    
    actions.push({
      type: 'unit-deployed',
      playerId: group.playerId,
      timestamp: Date.now(),
      unit: {
        id: group.source.id,
        name: group.source.name,
        instanceId: group.source.instanceId,
        type: 'strategem'
      },
      targets: group.targets, // Multiple targets for grouped deployments
      zone: group.zone,
      zoneName: group.zoneName,
      message: message,
      isGrouped: true // Flag to indicate this is a grouped deployment
    });
  });
  
  // Add individual deployments
  // ✅ DEBUG: Log before adding to actions
  if (individualDeployments.some(d => d.unit?.id === 239)) {
    console.log(`✅ ADDING ${individualDeployments.length} DEPLOYMENTS TO ACTIONS, including Kek`);
  }
  actions.push(...individualDeployments);
  
  // ✅ DEBUG: Log after adding to actions
  if (actions.some(a => a.type === 'unit-deployed' && a.unit?.id === 239)) {
    console.log(`✅ VERIFIED: Kek deployment is in actions array, total actions: ${actions.length}`);
  }
  
  // 2. Detect unit attacks
  // ✅ DISABLED: Attacks are now logged directly from WebSocket messages (attack-initiated)
  // This prevents duplicate logs when state updates arrive after the attack message
  // State-based detection is only used for deployments and deploy-effect kills
  
  // 2.5. Detect drone deployments
  allPlayerIds.forEach(playerId => {
    const prevPlayer = prevState.state?.[playerId];
    const currPlayer = currState.state?.[playerId];
    if (!prevPlayer || !currPlayer) return;
    
    const prevDrones = prevPlayer.drones || [];
    const currDrones = currPlayer.drones || [];
    
    // Find newly deployed drones
    currDrones.forEach(currDrone => {
      if (!currDrone || !currDrone.instanceId) return;
      
      const wasAlreadyDeployed = prevDrones.some(
        d => d && d.instanceId === currDrone.instanceId
      );
      
      if (!wasAlreadyDeployed && currDrone.deployedTurn === currentTurn) {
        // New drone deployed this turn
        actions.push({
          type: 'drone-deployed',
          playerId: playerId,
          timestamp: Date.now(),
          drone: {
            id: currDrone.id,
            name: currDrone.name,
            instanceId: currDrone.instanceId
          },
          message: `${currDrone.name} ${i18n.t('battleUI.actionHistory.hasBeenDeployed')}`
        });
      }
    });
  });
  
  // 2.6. Detect orders provided to hand
  allPlayerIds.forEach(playerId => {
    const prevPlayer = prevState.state?.[playerId];
    const currPlayer = currState.state?.[playerId];
    if (!prevPlayer || !currPlayer) return;
    
    // Check if player has newly provided orders
    const currProvidedOrders = currPlayer.providedOrders || [];
    const prevProvidedOrders = prevPlayer.providedOrders || [];
    
    // Find newly provided orders (provided this turn or in recent turns)
    // Use a more lenient check: if the order wasn't in prevState, it's new
    currProvidedOrders.forEach(providedOrder => {
      // Check if this order was already provided in previous state
      const wasAlreadyProvided = prevProvidedOrders.some(
        p => p.orderInstanceId === providedOrder.orderInstanceId
      );
      
      // If not in previous state and has providedBy info, it's a new order
      if (!wasAlreadyProvided && providedOrder.providedBy) {
        // Also check if it was provided this turn (or very recently)
        const providedTurn = providedOrder.providedTurn;
        if (providedTurn === currentTurn || providedTurn === currentTurn - 1 || !providedTurn) {
          // New order provided (localized)
          actions.push({
            type: 'order-provided',
            playerId: playerId,
            timestamp: Date.now(),
            source: {
              id: providedOrder.providedBy.id,
              name: providedOrder.providedBy.name,
              instanceId: providedOrder.providedBy.instanceId,
              type: providedOrder.providedBy.type
            },
            order: {
              id: providedOrder.orderId,
              name: providedOrder.orderName,
              instanceId: providedOrder.orderInstanceId
            },
            message: i18n.t('battleUI.actionHistory.sourceProvidedOrder', {
              sourceName: providedOrder.providedBy.name,
              orderName: providedOrder.orderName,
              defaultValue: `${providedOrder.providedBy.name} provided ${providedOrder.orderName}`
            })
          });
        }
      }
    });
  });
  
  // 2.7. Detect general interactions (healing/damage from deploy effects, strategems, drones, weapons, and unit attacks)
  allPlayerIds.forEach(playerId => {
    const prevPlayer = prevState.state?.[playerId];
    const currPlayer = currState.state?.[playerId];
    if (!prevPlayer || !currPlayer) return;
    
    // Check if player has new general interactions
    const currGeneralInteractions = currPlayer.generalInteractions || [];
    const prevGeneralInteractions = prevPlayer.generalInteractions || [];
    
    // ✅ LOG: Log strategem 321 detection
    if (currGeneralInteractions.some(i => i.sourceId === 321 || i.sourceName === 'Precision Strike')) {
      console.log(`🎯 Action Detection: Found strategem 321 (Precision Strike) in generalInteractions for player ${playerId}`, {
        currCount: currGeneralInteractions.length,
        prevCount: prevGeneralInteractions.length,
        interactions: currGeneralInteractions.filter(i => i.sourceId === 321 || i.sourceName === 'Precision Strike')
      });
    }
    
    // Find new general interactions (this turn or very recently)
    currGeneralInteractions.forEach(interaction => {
      // ✅ LOG: Log strategem 321 specifically
      const isStrategem321 = interaction.sourceId === 321 || interaction.sourceName === 'Precision Strike';
      if (isStrategem321) {
        console.log(`🎯 Action Detection: Checking strategem 321 interaction:`, {
          sourceId: interaction.sourceId,
          sourceName: interaction.sourceName,
          effectType: interaction.effectType,
          amount: interaction.amount,
          interactionTurn: interaction.interactionTurn,
          currentTurn: currentTurn
        });
      }
      
      // Check if this interaction was already in previous state
      const wasAlreadyInteracted = prevGeneralInteractions.some(
        p => p.sourceInstanceId === interaction.sourceInstanceId &&
             p.effectType === interaction.effectType &&
             p.amount === interaction.amount &&
             p.interactionTurn === interaction.interactionTurn
      );
      
      if (isStrategem321) {
        console.log(`🎯 Action Detection: Strategem 321 wasAlreadyInteracted: ${wasAlreadyInteracted}`);
      }
      
      // If not in previous state and has source info, it's a new interaction
      if (!wasAlreadyInteracted && interaction.sourceName) {
        // Also check if it was this turn (or very recently)
        const interactionTurn = interaction.interactionTurn;
        const isRecentTurn = interactionTurn === currentTurn || interactionTurn === currentTurn - 1 || !interactionTurn;
        
        if (isStrategem321) {
          console.log(`🎯 Action Detection: Strategem 321 isRecentTurn: ${isRecentTurn} (interactionTurn: ${interactionTurn}, currentTurn: ${currentTurn})`);
        }
        
        if (isRecentTurn) {
          // New general interaction
          const isAttack = interaction.effectType === 'attack';
          const isHeal = interaction.effectType === 'heal';
          const isDamage = interaction.effectType === 'damage';
          
          let message = '';
          if (isAttack) {
            const amt = interaction.amount != null ? interaction.amount : 0;
            message = i18n.t('battleUI.actionHistory.sourceDealtDamage', {
              sourceName: interaction.sourceName,
              amount: amt,
              defaultValue: `${interaction.sourceName} dealt ${amt} damage`
            });
          } else if (isHeal) {
            message = i18n.t('battleUI.actionHistory.sourceHealedGeneral', {
              sourceName: interaction.sourceName,
              amount: interaction.amount,
              defaultValue: `${interaction.sourceName} healed the general for ${interaction.amount}`
            });
          } else if (isDamage) {
            message = i18n.t('battleUI.actionHistory.sourceDealtDamage', {
              sourceName: interaction.sourceName,
              amount: interaction.amount,
              defaultValue: `${interaction.sourceName} dealt ${interaction.amount} damage`
            });
          }
          
          if (message) {
            if (isStrategem321) {
              console.log(`🎯 Action Detection: ✅ Adding strategem 321 action to history: "${message}"`);
            }
            actions.push({
              type: 'general-interaction',
              playerId: playerId,
              timestamp: Date.now(),
              source: {
                id: interaction.sourceId,
                name: interaction.sourceName,
                instanceId: interaction.sourceInstanceId,
                type: interaction.sourceType
              },
              effectType: interaction.effectType,
              amount: interaction.amount,
              message: message
            });
          } else if (isStrategem321) {
            console.log(`🎯 Action Detection: ❌ No message generated for strategem 321 (isAttack: ${isAttack}, isHeal: ${isHeal}, isDamage: ${isDamage})`);
          }
        }
      } else if (isStrategem321) {
        console.log(`🎯 Action Detection: ❌ Skipping strategem 321 - wasAlreadyInteracted: ${wasAlreadyInteracted}, hasSourceName: ${!!interaction.sourceName}`);
      }
    });
  });
  
  // 2.8. Detect weapon equipping
  allPlayerIds.forEach(playerId => {
    const prevPlayer = prevState.state?.[playerId];
    const currPlayer = currState.state?.[playerId];
    if (!prevPlayer || !currPlayer) return;
    
    ['front', 'flank'].forEach(zone => {
      if (!currPlayer.playedOrders?.[zone]) return;
      
      currPlayer.playedOrders[zone].forEach((currUnit) => {
        if (!currUnit || !currUnit.instanceId) return;
        
        // Check if this unit has newly equipped weapons
        const currEquippedWeapons = currUnit.equippedWeapons || [];
        
        // ✅ DEBUG: Log weapon equipping detection for bot players
        if ((playerId === 'bot' || String(playerId).includes('bot')) && currEquippedWeapons.length > 0) {
          console.log(`🔍 WEAPON DETECTION DEBUG (${playerId}):`, {
            unitName: currUnit.name,
            unitId: currUnit.id,
            equippedWeaponsCount: currEquippedWeapons.length,
            equippedWeapons: currEquippedWeapons.map(ew => ({
              weaponName: ew.weaponName,
              equippedTurn: ew.equippedTurn,
              equippedBy: ew.equippedBy,
              currentTurn: currentTurn
            }))
          });
        }
        
        // Find the same unit in previous state by instanceId (not by position)
        let prevUnit = null;
        if (prevPlayer.playedOrders?.[zone]) {
          prevUnit = prevPlayer.playedOrders[zone].find(
            u => u && u.instanceId === currUnit.instanceId
          );
        }
        const prevEquippedWeapons = prevUnit?.equippedWeapons || [];
        
        // Find newly equipped weapons (equipped this turn or previous turn)
        // ✅ FIX: Check currentTurn OR currentTurn - 1 to catch bot actions
        // Bot actions happen during their turn, but when the turn ends, turnNumber increments
        // So equippedTurn might be currentTurn - 1 for bot actions
        currEquippedWeapons.forEach(equippedWeapon => {
          const equippedTurn = equippedWeapon.equippedTurn;
          const isRecentTurn = equippedTurn === currentTurn || equippedTurn === currentTurn - 1;
          
          // ✅ DEBUG: Log turn number check for bot players
          if ((playerId === 'bot' || String(playerId).includes('bot'))) {
            console.log(`🔍 WEAPON TURN CHECK (${playerId}):`, {
              weaponName: equippedWeapon.weaponName,
              equippedTurn: equippedTurn,
              currentTurn: currentTurn,
              isRecentTurn: isRecentTurn,
              wasAlreadyEquipped: prevEquippedWeapons.some(w => w.weaponInstanceId === equippedWeapon.weaponInstanceId)
            });
          }
          
          if (isRecentTurn) {
            // Check if this weapon was already equipped in previous state
            const wasAlreadyEquipped = prevEquippedWeapons.some(
              w => w.weaponInstanceId === equippedWeapon.weaponInstanceId
            );
            
            if (!wasAlreadyEquipped) {
              // New weapon equipped this turn or previous turn
              console.log(`✅ WEAPON EQUIPPED DETECTED (${playerId}): ${equippedWeapon.weaponName} on ${currUnit.name}`);
              actions.push({
                type: 'weapon-equipped',
                playerId: equippedWeapon.equippedBy || playerId,
                timestamp: Date.now(),
                weapon: {
                  id: equippedWeapon.weaponId,
                  name: equippedWeapon.weaponName,
                  instanceId: equippedWeapon.weaponInstanceId
                },
                target: {
                  id: currUnit.id,
                  name: currUnit.name,
                  instanceId: currUnit.instanceId
                },
                message: `${equippedWeapon.weaponName} has been equipped to ${currUnit.name}`
              });
            }
          }
        });
      });
    });
  });
  
  // 3. Detect units killed (by attacks, deploy effects, or drone charges)
  // ✅ GROUP KILLS: Collect all kills first, then group by killer
  const killGroups = new Map(); // Maps killer key -> { killer, targets[], playerId }
  
  allPlayerIds.forEach(playerId => {
    const prevPlayer = prevState.state?.[playerId];
    const currPlayer = currState.state?.[playerId];
    if (!prevPlayer || !currPlayer) return;
    
    if (currPlayer.completedOrders) {
      currPlayer.completedOrders.forEach(destroyedUnit => {
        if (!destroyedUnit || !destroyedUnit.instanceId) return;
        
        // Check if this unit was already in previous state's completedOrders
        const wasAlreadyDestroyed = prevPlayer.completedOrders?.some(
          o => o && o.instanceId === destroyedUnit.instanceId
        );
        
        if (!wasAlreadyDestroyed) {
          // This unit was just destroyed
          let killerUnit = null;
          let killerPlayerId = null;
          
          // ✅ FIX: Only log kills when we have explicit data, not guesses
          // Check for drone kills first (explicit marking from backend)
          if (destroyedUnit.destructionType === 'drone' || 
              (destroyedUnit.destroyedBy && destroyedUnit.destroyedBy.startsWith('Drone '))) {
            // Extract drone info from destroyedBy (format: "Drone 79 (Aerial Strike Drone)")
            const droneMatch = destroyedUnit.destroyedBy.match(/Drone (\d+) \((.+)\)/);
            if (droneMatch) {
              const droneId = parseInt(droneMatch[1]);
              const droneName = droneMatch[2];
              
              // Find the drone in the player's drones array or completedOrders
              allPlayerIds.forEach(searchPlayerId => {
                const searchPlayer = currState.state?.[searchPlayerId];
                if (!searchPlayer || killerUnit) return;
                
                // Check active drones first
                const activeDrone = searchPlayer.drones?.find(
                  d => d && d.id === droneId
                );
                
                if (activeDrone) {
                  killerUnit = activeDrone;
                  killerPlayerId = searchPlayerId;
                } else {
                  // Check completed orders if drone was removed
                  const completedDrone = searchPlayer.completedOrders?.find(
                    o => o && o.id === droneId && o.type === 'Drone'
                  );
                  
                  if (completedDrone) {
                    killerUnit = completedDrone;
                    killerPlayerId = searchPlayerId;
                  }
                }
              });
            }
          } else if (destroyedUnit.destructionType === 'strategem' || 
              (destroyedUnit.destroyedBy && destroyedUnit.destroyedBy.startsWith('Strategem '))) {
            // Extract strategem info from destroyedBy (format: "Strategem 319 (Inferno Drop)")
            const strategemMatch = destroyedUnit.destroyedBy.match(/Strategem (\d+) \((.+)\)/);
            if (strategemMatch) {
              const strategemId = strategemMatch[1];
              const strategemName = strategemMatch[2];
              
              // Find the strategem in completedOrders to get full info
              allPlayerIds.forEach(searchPlayerId => {
                const searchPlayer = currState.state?.[searchPlayerId];
                if (!searchPlayer || killerUnit) return;
                
                const strategem = searchPlayer.completedOrders?.find(
                  o => o && o.id === parseInt(strategemId) && o.type === 'Strategem'
                );
                
                if (strategem) {
                  killerUnit = strategem;
                  killerPlayerId = searchPlayerId;
                }
              });
            }
          } else if (destroyedUnit.destructionType === 'combat') {
            // Only use explicit killer from backend (no guessing). Backend sets killerInstanceId when a unit is killed by another in combat.
            if (destroyedUnit.killerInstanceId || (destroyedUnit.killerId != null && destroyedUnit.killerName)) {
              const kid = destroyedUnit.killerInstanceId;
              const kidLegacy = destroyedUnit.killerId;
              allPlayerIds.forEach(searchPlayerId => {
                const searchPlayer = currState.state?.[searchPlayerId];
                if (!searchPlayer || killerUnit) return;
                ['front', 'flank'].forEach(searchZone => {
                  if (searchPlayer.playedOrders?.[searchZone]) {
                    const u = searchPlayer.playedOrders[searchZone].find(
                      o => o && (o.instanceId === kid || (!kid && o.id === kidLegacy))
                    );
                    if (u) {
                      killerUnit = u;
                      killerPlayerId = searchPlayerId;
                    }
                  }
                });
                if (!killerUnit && searchPlayer.completedOrders) {
                  const completed = searchPlayer.completedOrders.find(
                    o => o && (o.instanceId === kid || (!kid && o.id === kidLegacy))
                  );
                  if (completed) {
                    killerUnit = completed;
                    killerPlayerId = searchPlayerId;
                  }
                }
              });
              if (!killerUnit && destroyedUnit.killerName && destroyedUnit.killerPlayerId) {
                killerUnit = { id: destroyedUnit.killerId, name: destroyedUnit.killerName, instanceId: destroyedUnit.killerInstanceId };
                killerPlayerId = destroyedUnit.killerPlayerId;
              }
            }
            // If no explicit killer: do NOT guess. Combat kill may already be logged from attack-complete (two-phase).
          } else if (destroyedUnit.destructionType === 'deploy' && destroyedUnit.destroyedBy === 'Deploy Effect') {
            // Only attribute when we have explicit enhancedBy from backend (no guessing). Find source by instanceId from enhancedBy.
            const enhancedBy = Array.isArray(destroyedUnit.enhancedBy) ? destroyedUnit.enhancedBy : (destroyedUnit.enhancedBy ? [destroyedUnit.enhancedBy] : []);
            const damageEnhancement = enhancedBy.find(e => e && (e.effectType === 'damage' || (e.effectType === 'stat' && e.healthChange < 0)) && e.instanceId);
            if (damageEnhancement && damageEnhancement.instanceId) {
              allPlayerIds.forEach(searchPlayerId => {
                const searchPlayer = currState.state?.[searchPlayerId];
                if (!searchPlayer || killerUnit) return;
                ['front', 'flank'].forEach(searchZone => {
                  if (searchPlayer.playedOrders?.[searchZone]) {
                    const u = searchPlayer.playedOrders[searchZone].find(o => o && o.instanceId === damageEnhancement.instanceId);
                    if (u) {
                      killerUnit = u;
                      killerPlayerId = searchPlayerId;
                    }
                  }
                });
                if (!killerUnit && searchPlayer.completedOrders) {
                  const completed = searchPlayer.completedOrders.find(o => o && o.instanceId === damageEnhancement.instanceId);
                  if (completed) {
                    killerUnit = completed;
                    killerPlayerId = searchPlayerId;
                  }
                }
              });
            }
            // If no enhancedBy with instanceId: do NOT guess (could attribute to wrong unit, e.g. ally).
          } else if (destroyedUnit.destroyedBy === 'D.E.V.A.S.T') {
            // Unit 102 D.E.V.A.S.T - turn-start 4 damage to random enemy
            const opponentId = allPlayerIds.find(id => id !== playerId);
            if (opponentId) {
              const opponentState = currState.state?.[opponentId];
              if (opponentState) {
                ['front', 'flank'].forEach((searchZone) => {
                  if (opponentState.playedOrders?.[searchZone] && !killerUnit) {
                    const u = opponentState.playedOrders[searchZone].find(
                      o => o && o.id === 102
                    );
                    if (u) {
                      killerUnit = u;
                      killerPlayerId = opponentId;
                    }
                  }
                });
                if (!killerUnit && opponentState.completedOrders) {
                  const completed = opponentState.completedOrders.find(
                    o => o && o.id === 102
                  );
                  if (completed) {
                    killerUnit = completed;
                    killerPlayerId = opponentId;
                  }
                }
              }
            }
          } else if (destroyedUnit.destructionType === 'fortification_instakill' ||
              (destroyedUnit.destroyedBy && destroyedUnit.destroyedBy === 'Fortification Instakill')) {
            // Fortification Instakill: defender (other player) destroyed this unit; no killer unit to find
            const defenderId = allPlayerIds.find(id => id !== playerId);
            if (defenderId) {
              actions.push({
                type: 'fortification-instakill',
                playerId: defenderId,
                timestamp: Date.now(),
                actionItem: { id: 60, name: 'Instakill' },
                target: {
                  id: destroyedUnit.id,
                  name: destroyedUnit.name,
                  instanceId: destroyedUnit.instanceId,
                  died: true
                },
                message: i18n.t('battleUI.actionHistory.fortificationInstakillDestroyed', {
                  defaultValue: 'Fortification Instakill destroyed {{unitName}}',
                  unitName: destroyedUnit.name
                })
              });
            }
          }
          
          // If we found a killer (unit or strategem), add to kill group
          if (killerUnit) {
            const isStrategem = killerUnit.type === 'Strategem' ||
                                 (destroyedUnit.destructionType === 'strategem') ||
                                 (destroyedUnit.destroyedBy && destroyedUnit.destroyedBy.startsWith('Strategem '));
            const isDrone = destroyedUnit.destructionType === 'drone' ||
                             (destroyedUnit.destroyedBy && destroyedUnit.destroyedBy.startsWith('Drone '));
            // Unit-vs-unit: skip separate "unit1 destroyed unit2" entry; damage entry already covers it.
            const isCombatUnitVsUnit = destroyedUnit.destructionType === 'combat' && !isStrategem && !isDrone;
            if (isCombatUnitVsUnit) {
              // Do not add to killGroups; the unit-attacked entry (dealt/suffered damage) is sufficient.
            } else {
            // Create a unique key for grouping (instanceId for units, id for strategems)
            const killerKey = killerUnit.instanceId || `strategem-${killerUnit.id}`;
            
            if (!killGroups.has(killerKey)) {
              
              killGroups.set(killerKey, {
                killer: {
                  id: killerUnit.id,
                  name: killerUnit.name,
                  instanceId: killerUnit.instanceId,
                  isStrategem: isStrategem,
                  isDrone: isDrone
                },
                targets: [],
                playerId: killerPlayerId,
                isStrategem: isStrategem,
                isDrone: isDrone
              });
            }
            
            // Add this destroyed unit to the group's targets
            // For deploy effect kills, add health information
            const targetInfo = {
              id: destroyedUnit.id,
              name: destroyedUnit.name,
              instanceId: destroyedUnit.instanceId,
              died: true
            };
            
            // If destroyed by deploy effect, try to get health information
            if (destroyedUnit.destructionType === 'deploy' && destroyedUnit.destroyedBy === 'Deploy Effect') {
              // Find the unit in previous state to get health before destruction
              let prevUnitForHealth = null;
              ['front', 'flank'].forEach(checkZone => {
                if (!prevUnitForHealth && prevPlayer.playedOrders?.[checkZone]) {
                  prevUnitForHealth = prevPlayer.playedOrders[checkZone].find(
                    u => u && u.instanceId === destroyedUnit.instanceId
                  );
                }
              });
              
              if (prevUnitForHealth) {
                // Get health before destruction
                targetInfo.healthBefore = prevUnitForHealth.health || prevUnitForHealth.maxHealth || 0;
                
                // Try to get damage amount from enhancedBy on the previous unit (before destruction)
                // The enhancedBy might be on the unit before it was destroyed
                const prevEnhancedBy = Array.isArray(prevUnitForHealth.enhancedBy) 
                  ? prevUnitForHealth.enhancedBy 
                  : (prevUnitForHealth.enhancedBy ? [prevUnitForHealth.enhancedBy] : []);
                
                // Also check destroyed unit's enhancedBy as fallback
                const destroyedEnhancedBy = Array.isArray(destroyedUnit.enhancedBy) 
                  ? destroyedUnit.enhancedBy 
                  : (destroyedUnit.enhancedBy ? [destroyedUnit.enhancedBy] : []);
                
                const allEnhancedBy = [...prevEnhancedBy, ...destroyedEnhancedBy];
                
                // Look for damage or stat effect that reduced health
                for (const enhancement of allEnhancedBy) {
                  if (enhancement && enhancement.instanceId === killerUnit?.instanceId) {
                    if (enhancement.effectType === 'damage' && enhancement.amount) {
                      targetInfo.damageTaken = enhancement.amount;
                      break;
                    } else if (enhancement.effectType === 'stat' && enhancement.healthChange < 0) {
                      targetInfo.damageTaken = Math.abs(enhancement.healthChange);
                      break;
                    }
                  }
                }
                
                // If we couldn't find damage amount from enhancedBy, use healthBefore as damageTaken
                // (unit was destroyed, so it took at least that much damage)
                if (!targetInfo.damageTaken && targetInfo.healthBefore > 0) {
                  targetInfo.damageTaken = targetInfo.healthBefore;
                }
                
                console.log(`📊 Deploy kill health info:`, {
                  unitName: destroyedUnit.name,
                  unitId: destroyedUnit.id,
                  healthBefore: targetInfo.healthBefore,
                  damageTaken: targetInfo.damageTaken,
                  foundInPrevState: !!prevUnitForHealth,
                  prevHealth: prevUnitForHealth.health,
                  prevMaxHealth: prevUnitForHealth.maxHealth,
                  hasPrevEnhancedBy: prevEnhancedBy.length > 0,
                  hasDestroyedEnhancedBy: destroyedEnhancedBy.length > 0
                });
              } else {
                console.warn(`⚠️ Could not find previous unit for deploy kill health info:`, {
                  unitName: destroyedUnit.name,
                  unitId: destroyedUnit.id,
                  instanceId: destroyedUnit.instanceId
                });
              }
            }
            
            killGroups.get(killerKey).targets.push(targetInfo);
            }
          }
        }
      });
    }
  });
  
  // ✅ GROUP KILLS: Convert grouped kills into actions (localized)
  killGroups.forEach((group) => {
    const targetCount = group.targets.length;
    const killerName = group.killer.name;
    
    let message;
    if (targetCount === 1) {
      const targetName = group.targets[0].name;
      message = group.isStrategem
        ? i18n.t('battleUI.actionHistory.killerHasDestroyed', { killerName, targetName, defaultValue: `${killerName} has destroyed ${targetName}` })
        : i18n.t('battleUI.actionHistory.killerHasKilled', { killerName, targetName, defaultValue: `${killerName} has killed ${targetName}` });
    } else {
      message = group.isStrategem
        ? i18n.t('battleUI.actionHistory.killerHasDestroyedUnits', { killerName, count: targetCount, defaultValue: `${killerName} has destroyed ${targetCount} units` })
        : i18n.t('battleUI.actionHistory.killerHasKilledUnits', { killerName, count: targetCount, defaultValue: `${killerName} has killed ${targetCount} units` });
    }
    
    actions.push({
      type: 'unit-killed',
      playerId: group.playerId,
      timestamp: Date.now(),
      killer: group.killer,
      targets: group.targets, // Multiple targets for grouped kills
      message: message
    });
  });
  
  // 4. Detect deploy effects (non-kill effects like ability grants, stat changes, status effects)
  // ✅ GROUP EFFECTS: Collect all effects first, then group by enhancer and effect type
  const effectGroups = new Map(); // Maps group key -> { enhancer, targets[], playerId, effect }
  
  allPlayerIds.forEach(playerId => {
    const prevPlayer = prevState.state?.[playerId];
    const currPlayer = currState.state?.[playerId];
    if (!prevPlayer || !currPlayer) return;
    
    // 4a. Damage to destroyed units (e.g. D.E.V.A.S.T turn-start damage that kills)
    // Units in completedOrders with enhancedBy effectType 'damage' were damaged then destroyed
    const prevCompleted = prevPlayer.completedOrders || [];
    (currPlayer.completedOrders || []).forEach((destroyedUnit) => {
      if (!destroyedUnit || !destroyedUnit.instanceId) return;
      const wasAlreadyDestroyed = prevCompleted.some(
        o => o && o.instanceId === destroyedUnit.instanceId
      );
      if (wasAlreadyDestroyed) return;
      const enhancedBy = Array.isArray(destroyedUnit.enhancedBy) ? destroyedUnit.enhancedBy : (destroyedUnit.enhancedBy ? [destroyedUnit.enhancedBy] : []);
      enhancedBy.forEach((enhancement) => {
        if (!enhancement || enhancement.effectType !== 'damage' || !enhancement.instanceId || !enhancement.amount) return;
        const enhancementTurn = enhancement.turn;
        const isRecentTurn = enhancementTurn === currentTurn || enhancementTurn === currentTurn - 1;
        if (!isRecentTurn) return;
        // Source is a unit (e.g. D.E.V.A.S.T) - find by instanceId
        let sourceUnit = null;
        let sourcePlayerId = null;
        allPlayerIds.forEach((searchPlayerId) => {
          const searchPlayer = currState.state?.[searchPlayerId];
          if (!searchPlayer || sourceUnit) return;
          ['front', 'flank'].forEach((searchZone) => {
            if (searchPlayer.playedOrders?.[searchZone]) {
              const u = searchPlayer.playedOrders[searchZone].find(
                o => o && o.instanceId === enhancement.instanceId
              );
              if (u) {
                sourceUnit = u;
                sourcePlayerId = searchPlayerId;
              }
            }
          });
          if (!sourceUnit) {
            const completed = searchPlayer.completedOrders?.find(
              o => o && o.instanceId === enhancement.instanceId
            );
            if (completed) {
              sourceUnit = completed;
              sourcePlayerId = searchPlayerId;
            }
          }
        });
        if (!sourceUnit) return;
        const effectKey = `${sourceUnit.instanceId || sourceUnit.id}-damage-${enhancement.amount}`;
        if (!effectGroups.has(effectKey)) {
          effectGroups.set(effectKey, {
            enhancer: { id: sourceUnit.id, name: sourceUnit.name, instanceId: sourceUnit.instanceId, isDrone: false },
            targets: [],
            playerId: sourcePlayerId,
            effect: { ...enhancement, effectType: 'damage' },
            isWeapon: false
          });
        }
        let prevUnitForHealth = null;
        ['front', 'flank'].forEach((checkZone) => {
          if (!prevUnitForHealth && prevPlayer.playedOrders?.[checkZone]) {
            prevUnitForHealth = prevPlayer.playedOrders[checkZone].find(
              u => u && u.instanceId === destroyedUnit.instanceId
            );
          }
        });
        const targetInfo = {
          id: destroyedUnit.id,
          name: destroyedUnit.name,
          instanceId: destroyedUnit.instanceId,
          died: true
        };
        if (prevUnitForHealth) {
          targetInfo.healthBefore = prevUnitForHealth.health ?? prevUnitForHealth.maxHealth ?? 0;
          targetInfo.damageTaken = enhancement.amount;
        } else {
          targetInfo.healthBefore = destroyedUnit.maxHealth ?? 0;
          targetInfo.damageTaken = enhancement.amount;
        }
        effectGroups.get(effectKey).targets.push(targetInfo);
      });
    });
    
    ['front', 'flank'].forEach(zone => {
      if (currPlayer.playedOrders?.[zone]) {
        currPlayer.playedOrders[zone].forEach((unit, index) => {
          if (!unit || !unit.instanceId) return;
          
          // Check if this unit has enhancedBy that wasn't in previous state
          // enhancedBy can be a single object or an array of effects
          const enhancedBy = Array.isArray(unit.enhancedBy) ? unit.enhancedBy : (unit.enhancedBy ? [unit.enhancedBy] : []);
          
          enhancedBy.forEach((enhancement) => {
            if (enhancement) {
              // ✅ FIX: Check currentTurn OR currentTurn - 1 to catch bot actions
              // Bot actions happen during their turn, but when the turn ends, turnNumber increments
              const enhancementTurn = enhancement.turn;
              const isRecentTurn = enhancementTurn === currentTurn || enhancementTurn === currentTurn - 1;
              if (!isRecentTurn) return;
              // Find the same unit in previous state
              const prevUnit = prevPlayer.playedOrders?.[zone]?.[index];
              const prevEnhancedBy = prevUnit?.enhancedBy ? 
                (Array.isArray(prevUnit.enhancedBy) ? prevUnit.enhancedBy : [prevUnit.enhancedBy]) : [];
              const wasAlreadyEnhanced = prevEnhancedBy.some(e => 
                e && e.instanceId === enhancement.instanceId && e.turn === enhancement.turn &&
                e.effectType === enhancement.effectType &&
                (enhancement.effectType !== 'status' || e.status === enhancement.status || 
                 (e.statuses && e.statuses.includes(enhancement.status))) &&
                (enhancement.effectType !== 'stat' || (e.attackChange === enhancement.attackChange && e.healthChange === enhancement.healthChange)) &&
                (enhancement.effectType !== 'ability' || e.ability === enhancement.ability)
              );
              
              // ✅ DEBUG: Log damage effect detection
              if (enhancement.effectType === 'damage') {
                console.log(`🔍 DAMAGE EFFECT DEBUG:`, {
                  unitName: unit.name,
                  unitId: unit.id,
                  enhancement,
                  wasAlreadyEnhanced,
                  hasInstanceId: !!enhancement.instanceId,
                  hasAmount: !!enhancement.amount,
                  amount: enhancement.amount,
                  prevUnit: prevUnit ? { name: prevUnit.name, id: prevUnit.id, health: prevUnit.health } : null
                });
              }
              
              // ✅ DEBUG: Log stat effect detection for unit 166 (Kendrex Toxengineer)
              if (enhancement.effectType === 'stat' && (enhancement.attackChange === -1 || enhancement.healthChange === -1)) {
                console.log(`🔍 STAT EFFECT DEBUG:`, {
                  unitName: unit.name,
                  unitId: unit.id,
                  enhancement,
                  wasAlreadyEnhanced,
                  hasInstanceId: !!enhancement.instanceId,
                  prevUnit: prevUnit ? { name: prevUnit.name, id: prevUnit.id } : null
                });
              }
              
              if (!wasAlreadyEnhanced && enhancement.instanceId) {
                // Check if this is a drone effect, weapon effect, or strategem effect
                const isDroneEffect = enhancement.sourceType === 'drone';
                const isWeaponEffect = enhancement.sourceType === 'weapon';
                const isStrategemEffect = enhancement.sourceType === 'strategem';
                
                let sourceUnit = null;
                let sourceDrone = null;
                let sourceWeapon = null;
                let sourceStrategem = null;
                let sourcePlayerId = null;
                
                if (isDroneEffect) {
                  // Find the drone that applied the effect
                  allPlayerIds.forEach(searchPlayerId => {
                    const searchPlayer = currState.state?.[searchPlayerId];
                    if (!searchPlayer || sourceDrone) return;
                    
                    // Check active drones
                    const activeDrone = searchPlayer.drones?.find(
                      d => d && d.instanceId === enhancement.instanceId
                    );
                    
                    if (activeDrone) {
                      sourceDrone = activeDrone;
                      sourcePlayerId = searchPlayerId;
                    } else {
                      // Check completed orders if drone was removed
                      const completedDrone = searchPlayer.completedOrders?.find(
                        o => o && o.instanceId === enhancement.instanceId && o.type === 'Drone'
                      );
                      
                      if (completedDrone) {
                        sourceDrone = completedDrone;
                        sourcePlayerId = searchPlayerId;
                      }
                    }
                  });
                } else if (isWeaponEffect) {
                  // Find the weapon that applied the effect (weapons are attached to units)
                  allPlayerIds.forEach(searchPlayerId => {
                    const searchPlayer = currState.state?.[searchPlayerId];
                    if (!searchPlayer || sourceWeapon) return;
                    
                    ['front', 'flank'].forEach(searchZone => {
                      if (searchPlayer.playedOrders?.[searchZone]) {
                        searchPlayer.playedOrders[searchZone].forEach((searchUnit) => {
                          if (!searchUnit || sourceWeapon) return;
                          // Check if this unit has the weapon attached
                          if (searchUnit.weapons && Array.isArray(searchUnit.weapons)) {
                            const weapon = searchUnit.weapons.find(
                              w => w && w.instanceId === enhancement.instanceId
                            );
                            if (weapon) {
                              sourceWeapon = weapon;
                              sourcePlayerId = searchPlayerId;
                            }
                          }
                        });
                      }
                    });
                  });
                  
                  // If weapon not found in active units, check completedOrders
                  if (!sourceWeapon) {
                    allPlayerIds.forEach(searchPlayerId => {
                      const searchPlayer = currState.state?.[searchPlayerId];
                      if (!searchPlayer || sourceWeapon) return;
                      
                      const completed = searchPlayer.completedOrders?.find(
                        o => o && o.instanceId === enhancement.instanceId && o.type === 'Weapon'
                      );
                      if (completed) {
                        sourceWeapon = completed;
                        sourcePlayerId = searchPlayerId;
                      }
                    });
                  }
                } else if (isStrategemEffect) {
                  // Find the strategem that applied the effect (strategems are in completedOrders)
                  allPlayerIds.forEach(searchPlayerId => {
                    const searchPlayer = currState.state?.[searchPlayerId];
                    if (!searchPlayer || sourceStrategem) return;
                    
                    // Strategems are stored in completedOrders after being played
                    const strategem = searchPlayer.completedOrders?.find(
                      o => o && o.instanceId === enhancement.instanceId && o.type === 'Strategem'
                    );
                    
                    if (strategem) {
                      sourceStrategem = strategem;
                      sourcePlayerId = searchPlayerId;
                    }
                  });
                } else {
                  // Find the source unit that applied the effect
                  allPlayerIds.forEach(searchPlayerId => {
                    const searchPlayer = currState.state?.[searchPlayerId];
                    if (!searchPlayer || sourceUnit) return;
                    
                    ['front', 'flank'].forEach(searchZone => {
                      if (searchPlayer.playedOrders?.[searchZone]) {
                        searchPlayer.playedOrders[searchZone].forEach((searchUnit) => {
                          if (!searchUnit || sourceUnit) return;
                          if (searchUnit.instanceId === enhancement.instanceId) {
                            sourceUnit = searchUnit;
                            sourcePlayerId = searchPlayerId;
                          }
                        });
                      }
                    });
                  });
                  
                  // If source unit not found, it might be in completedOrders (if it was destroyed)
                  if (!sourceUnit) {
                    allPlayerIds.forEach(searchPlayerId => {
                      const searchPlayer = currState.state?.[searchPlayerId];
                      if (!searchPlayer || sourceUnit) return;
                      
                      const completed = searchPlayer.completedOrders?.find(
                        o => o && o.instanceId === enhancement.instanceId
                      );
                      if (completed) {
                        sourceUnit = completed;
                        sourcePlayerId = searchPlayerId;
                      }
                    });
                  }
                }
                
                // Process effect if we found a source (unit, drone, weapon, or strategem)
                const source = sourceDrone || sourceWeapon || sourceStrategem || sourceUnit;
                
                // ✅ DEBUG: Log when source is not found for stat effects
                if (!source && enhancement.effectType === 'stat' && (enhancement.attackChange === -1 || enhancement.healthChange === -1)) {
                  console.log(`⚠️ STAT EFFECT: Source not found for enhancement:`, {
                    enhancement,
                    searchedForInstanceId: enhancement.instanceId,
                    isDroneEffect,
                    isWeaponEffect,
                    isStrategemEffect
                  });
                }
                
                if (source) {
                  // ✅ DEBUG: Log when source is found for stat effects
                  if (enhancement.effectType === 'stat' && (enhancement.attackChange === -1 || enhancement.healthChange === -1)) {
                    console.log(`✅ STAT EFFECT: Found source:`, {
                      sourceName: source.name,
                      sourceId: source.id,
                      sourceType: sourceDrone ? 'drone' : sourceWeapon ? 'weapon' : sourceStrategem ? 'strategem' : 'unit',
                      enhancement
                    });
                  }
                  const effect = enhancement;
                  // Handle multiple status effects (e.g., confused and immobilized)
                  let statuses = [];
                  if (effect.effectType === 'status') {
                    if (effect.statuses && Array.isArray(effect.statuses)) {
                      statuses = effect.statuses;
                    } else if (effect.status) {
                      statuses = [effect.status];
                    }
                  }
                  
                  // Create a unique key for grouping
                  // For status effects with multiple statuses, combine them
                  let effectKey = '';
                  const sourceId = source.instanceId || source.id;
                  if (effect.effectType === 'ability') {
                    effectKey = `${sourceId}-ability-${effect.ability}`;
                  } else if (effect.effectType === 'stat') {
                    effectKey = `${sourceId}-stat-${effect.attackChange}-${effect.healthChange}`;
                  } else if (effect.effectType === 'status') {
                    // For status effects, group by all statuses together
                    const statusKey = statuses.sort().join('-and-');
                    effectKey = `${sourceId}-status-${statusKey}`;
                  } else if (effect.effectType === 'damage') {
                    effectKey = `${sourceId}-damage-${effect.amount}`;
                  } else if (effect.effectType === 'shield') {
                    effectKey = `${sourceId}-shield`;
                  } else {
                    effectKey = `${sourceId}-${effect.effectType}`;
                  }
                  
                  if (!effectGroups.has(effectKey)) {
                    const isDrone = !!sourceDrone;
                    const isWeapon = !!sourceWeapon;
                    const isStrategem = !!sourceStrategem;
                    effectGroups.set(effectKey, {
                      enhancer: sourceDrone ? {
                        id: sourceDrone.id,
                        name: sourceDrone.name,
                        instanceId: sourceDrone.instanceId,
                        isDrone: true
                      } : sourceWeapon ? {
                        id: sourceWeapon.id,
                        name: sourceWeapon.name,
                        instanceId: sourceWeapon.instanceId,
                        isWeapon: true
                      } : sourceStrategem ? {
                        id: sourceStrategem.id,
                        name: sourceStrategem.name,
                        instanceId: sourceStrategem.instanceId,
                        isStrategem: true
                      } : {
                        id: sourceUnit.id,
                        name: sourceUnit.name,
                        instanceId: sourceUnit.instanceId,
                        isDrone: false
                      },
                      targets: [],
                      playerId: sourcePlayerId,
                      effect: {
                        ...effect,
                        statuses: statuses.length > 0 ? statuses : undefined
                      },
                      isWeapon: isWeapon
                    });
                  }
                  
                  // Add this unit to the group's targets
                  // For damage effects, include health information
                  const targetInfo = {
                    id: unit.id,
                    name: unit.name,
                    instanceId: unit.instanceId
                  };
                  
                  // If this is a damage effect OR a stat effect that reduces health, add health information
                  const isDamageEffect = effect.effectType === 'damage' && effect.amount;
                  const isHealthReducingStatEffect = effect.effectType === 'stat' && effect.healthChange < 0;
                  
                  if (isDamageEffect || isHealthReducingStatEffect) {
                    const damageAmount = isDamageEffect ? effect.amount : Math.abs(effect.healthChange);
                    
                    console.log(`🔍 Checking for damage/health-reducing effect:`, {
                      effectType: effect.effectType,
                      amount: effect.amount,
                      healthChange: effect.healthChange,
                      isDamageEffect,
                      isHealthReducingStatEffect,
                      damageAmount,
                      unitName: unit.name
                    });
                    // Always find by instanceId to ensure we get the correct unit (unit might have moved positions)
                    // Check all zones in case unit was repositioned
                    let prevUnitForHealth = null;
                    ['front', 'flank'].forEach(checkZone => {
                      if (!prevUnitForHealth) {
                        prevUnitForHealth = prevPlayer.playedOrders?.[checkZone]?.find(
                          u => u && u.instanceId === unit.instanceId
                        );
                      }
                    });
                    
                    // If not found in previous state, use prevUnit found by index as fallback
                    if (!prevUnitForHealth) {
                      prevUnitForHealth = prevUnit;
                    }
                    
                    if (prevUnitForHealth) {
                      targetInfo.healthBefore = prevUnitForHealth.health || prevUnitForHealth.maxHealth || 0;
                      targetInfo.damageTaken = damageAmount;
                    } else {
                      // Unit doesn't exist in previous state (newly deployed) - use current health + damage as healthBefore
                      // This handles cases where a unit is deployed and immediately takes damage
                      const currentHealth = unit.health || unit.maxHealth || 0;
                      targetInfo.healthBefore = currentHealth + damageAmount;
                      targetInfo.damageTaken = damageAmount;
                    }
                    
                    console.log(`📊 Damage/health-reducing effect health info:`, {
                      unitName: unit.name,
                      unitId: unit.id,
                      effectType: effect.effectType,
                      healthBefore: targetInfo.healthBefore,
                      damageTaken: targetInfo.damageTaken,
                      foundInPrevState: !!prevUnitForHealth,
                      currentHealth: unit.health,
                      currentMaxHealth: unit.maxHealth
                    });
                  }
                  
                  effectGroups.get(effectKey).targets.push(targetInfo);
                }
              }
            }
          });
        });
      }
    });
  });
  
  // ✅ GROUP EFFECTS: Convert grouped effects into actions
  effectGroups.forEach((group) => {
    const targetCount = group.targets.length;
    const effect = group.effect;
    const isDroneEffect = group.enhancer.isDrone;
    const isWeaponEffect = group.enhancer.isWeapon || group.isWeapon;
    
    // Generate message based on effect type and number of targets
    let message = '';
    if (targetCount === 1) {
      // Single target - use specific message
      if (effect.effectType === 'ability') {
        message = `${group.enhancer.name} gave ${effect.ability} to ${group.targets[0].name}`;
      } else if (effect.effectType === 'stat') {
        const attackText = effect.attackChange > 0 ? `+${effect.attackChange}` : `${effect.attackChange}`;
        const healthText = effect.healthChange > 0 ? `+${effect.healthChange}` : `${effect.healthChange}`;
        if (effect.attackChange === 0 && effect.healthChange !== 0) {
          message = `${group.enhancer.name} provided ${healthText} health to ${group.targets[0].name}`;
        } else if (effect.healthChange === 0 && effect.attackChange !== 0) {
          message = `${group.enhancer.name} provided ${attackText} attack to ${group.targets[0].name}`;
        } else {
          message = `${group.enhancer.name} provided ${attackText}/${healthText} to ${group.targets[0].name}`;
        }
      } else if (effect.effectType === 'status') {
        // Handle multiple status effects (e.g., "confused and immobilized")
        const statusText = effect.statuses && effect.statuses.length > 1 
          ? effect.statuses.join(' and ')
          : (effect.status || effect.statuses?.[0] || 'affected');
        message = `${group.enhancer.name} ${statusText} ${group.targets[0].name}`;
      } else if (effect.effectType === 'damage') {
        message = `${group.enhancer.name} dealt ${effect.amount} damage to ${group.targets[0].name}`;
      } else if (effect.effectType === 'shield') {
        message = `${group.enhancer.name} gave Shield to ${group.targets[0].name}`;
      }
    } else {
      // Multiple targets - use summary message
      if (effect.effectType === 'ability') {
        message = `${group.enhancer.name} gave ${effect.ability} to ${targetCount} units`;
      } else if (effect.effectType === 'stat') {
        const attackText = effect.attackChange > 0 ? `+${effect.attackChange}` : `${effect.attackChange}`;
        const healthText = effect.healthChange > 0 ? `+${effect.healthChange}` : `${effect.healthChange}`;
        if (effect.attackChange === 0 && effect.healthChange !== 0) {
          message = `${group.enhancer.name} provided ${healthText} health to ${targetCount} units`;
        } else if (effect.healthChange === 0 && effect.attackChange !== 0) {
          message = `${group.enhancer.name} provided ${attackText} attack to ${targetCount} units`;
        } else {
          message = `${group.enhancer.name} provided ${attackText}/${healthText} to ${targetCount} units`;
        }
      } else if (effect.effectType === 'status') {
        // Handle multiple status effects (e.g., "confused and immobilized")
        const statusText = effect.statuses && effect.statuses.length > 1 
          ? effect.statuses.join(' and ')
          : (effect.status || effect.statuses?.[0] || 'affected');
        message = `${group.enhancer.name} ${statusText} ${targetCount} units`;
      } else if (effect.effectType === 'damage') {
        message = `${group.enhancer.name} dealt ${effect.amount} damage to ${targetCount} units`;
      } else if (effect.effectType === 'shield') {
        message = `${group.enhancer.name} gave Shield to ${targetCount} units`;
      }
    }
    
    if (message) {
      // Use 'drone-charge-used' for drone effects, 'weapon-effect' for weapon effects, 'unit-enhanced' for unit effects
      const actionType = isDroneEffect ? 'drone-charge-used' : (isWeaponEffect ? 'weapon-effect' : 'unit-enhanced');
      actions.push({
        type: actionType,
        playerId: group.playerId,
        timestamp: Date.now(),
        enhancer: group.enhancer,
        weapon: isWeaponEffect ? group.enhancer : undefined,
        drone: isDroneEffect ? group.enhancer : undefined,
        targets: group.targets, // Multiple targets for grouped effects
        effect: effect,
        message: message
      });
    }
  });
  
  // ✅ FIX: Sort actions to ensure correct chronological order
  // Actions are detected in a specific order (strategems, deployments, general interactions, kills)
  // but the actual execution order may differ. This sorting ensures actions appear in the
  // correct chronological order based on their logical execution sequence.
  sortActionsByChronologicalOrder(actions);

  return actions;
}

/**
 * Chronological type priority for action history display.
 * Lower number = happens earlier in the turn (strategem → deploy → attack → effects).
 * Used by detectNewActions and by BattleUI to re-sort the recent tail when bot
 * actions arrive out of order (attack-initiated first, then state-diff with strategem/deploys).
 */
export const ACTION_TYPE_PRIORITY = {
  'strategem-requested': 1,
  'unit-deployed': 2,
  'drone-deployed': 3,
  'weapon-equipped': 4,
  'order-provided': 5,
  'unit-attacked': 6,   // e.g. "Kamikaze on infected" before destroy/damage
  'unit-killed': 7,    // e.g. "Kamikaze destroyed infected" after attack
  'general-interaction': 8, // e.g. "Kamikaze dealt 5 damage to general" after kill
  'drone-charge-used': 9,
  'weapon-effect': 10,
  'unit-enhanced': 11
};

/**
 * Compare two actions by chronological priority (strategem first, then deploys, then attacks, etc.).
 * Use for sorting a slice of action history so bot-turn order is correct.
 * Uses sequence as final tie-breaker so the sort is STABLE (same type + same timestamp no longer reorder randomly).
 */
export function compareActionsByChronologicalOrder(a, b) {
  const aPriority = ACTION_TYPE_PRIORITY[a.type] ?? 99;
  const bPriority = ACTION_TYPE_PRIORITY[b.type] ?? 99;
  if (aPriority !== bPriority) return aPriority - bPriority;
  const aStrategemId = a.order?.instanceId || a.killer?.instanceId || a.source?.instanceId;
  const bStrategemId = b.order?.instanceId || b.killer?.instanceId || b.source?.instanceId;
  if (aStrategemId && bStrategemId && aStrategemId === bStrategemId) return 0;
  const tsDiff = (a.timestamp || 0) - (b.timestamp || 0);
  if (tsDiff !== 0) return tsDiff;
  // Stable sort: preserve insertion order when type and timestamp are equal (avoids random order)
  return (a.sequence ?? 0) - (b.sequence ?? 0);
}

/**
 * Sort an array of actions in place by chronological order (strategem → deploy → attack → effects).
 * Used to fix action history order when bot sends attack-initiated before turn-played state.
 */
export function sortActionsByChronologicalOrder(actions) {
  if (!Array.isArray(actions) || actions.length <= 1) return actions;
  actions.sort(compareActionsByChronologicalOrder);
  return actions;
}

/**
 * Filter out duplicate actions from history
 */
export function filterDuplicates(newActions, existingHistory) {
  const existingKeys = new Set(
    existingHistory.map(action => {
      const unitId = action.unit?.instanceId || action.killer?.instanceId || action.actionItem?.instanceId || action.attacker?.instanceId || action.enhancer?.instanceId || action.drone?.instanceId || action.weapon?.instanceId || action.source?.instanceId;
      const type = action.type;
      const targetId = action.target?.instanceId || action.targets?.[0]?.instanceId || '';
      // For enhanced actions, also include effect type to distinguish different effects from same source
      // For weapon-equipped actions, include weapon instanceId to distinguish different weapons
      // For order-provided actions, include order instanceId to distinguish different orders
      // For general-interaction actions, include effectType and amount to distinguish different interactions
      const effectType = action.effect?.effectType || action.effectType || '';
      const weaponId = action.weapon?.instanceId || '';
      const orderId = action.order?.instanceId || '';
      const amount = action.amount || '';
      return `${unitId}-${type}-${targetId}-${effectType}-${weaponId}-${orderId}-${amount}`;
    })
  );
  
  return newActions.filter(action => {
    const unitId = action.unit?.instanceId || action.killer?.instanceId || action.actionItem?.instanceId || action.attacker?.instanceId || action.enhancer?.instanceId || action.drone?.instanceId || action.weapon?.instanceId || action.source?.instanceId;
    const type = action.type;
    const targetId = action.target?.instanceId || action.targets?.[0]?.instanceId || '';
    const effectType = action.effect?.effectType || action.effectType || '';
    const weaponId = action.weapon?.instanceId || '';
    const orderId = action.order?.instanceId || '';
    const amount = action.amount || '';
    const key = `${unitId}-${type}-${targetId}-${effectType}-${weaponId}-${orderId}-${amount}`;
    return !existingKeys.has(key);
  });
}

/**
 * Find an order/unit by instanceId in game state (hand, playedOrders, completedOrders across all players).
 * Returns { order, playerId } or null.
 */
export function findInGameState(gameState, instanceId) {
  if (!gameState?.state || !instanceId) return null;
  const playerIds = gameState.players || Object.keys(gameState.state);
  for (const pid of playerIds) {
    const p = gameState.state[pid];
    if (!p) continue;
    for (const o of p.hand || []) {
      if (o?.instanceId === instanceId) return { order: o, playerId: pid };
    }
    for (const zone of ['front', 'flank']) {
      const arr = p.playedOrders?.[zone];
      if (arr) for (const u of arr) { if (u?.instanceId === instanceId) return { order: u, playerId: pid }; }
    }
    for (const o of p.completedOrders || []) {
      if (o?.instanceId === instanceId) return { order: o, playerId: pid };
    }
    for (const d of p.drones || []) {
      if (d?.instanceId === instanceId) return { order: d, playerId: pid };
    }
    // Weapons are on units in playedOrders; search so weapon-equipped tooltip can resolve weapon order design
    for (const zone of ['front', 'flank']) {
      const arr = p.playedOrders?.[zone];
      if (arr) {
        for (const u of arr) {
          if (u?.weapons) {
            for (const w of u.weapons) {
              if (w?.instanceId === instanceId) return { order: w, playerId: pid };
            }
          }
        }
      }
    }
  }
  return null;
}

/**
 * Hydrate a server actionLog entry into the full action shape expected by ActionHistory/ActionHistoryTooltip.
 * Uses gameState to resolve instanceIds to order/unit/target objects.
 */
export function hydrateActionLogEntry(entry, gameState) {
  if (!entry || !gameState) return null;
  const action = {
    type: entry.type,
    playerId: entry.playerId,
    timestamp: entry.timestamp,
    sequence: entry.sequence
  };
  const inst = entry.instanceId || entry.attackerInstanceId;
  const targetInst = entry.targetInstanceId || entry.targetUnitInstanceId;
  if (inst) {
    const found = findInGameState(gameState, inst);
    if (found) {
      action.unit = action.order = action.attacker = action.source = found.order;
    }
  }
  if (targetInst) {
    const targetFound = findInGameState(gameState, targetInst);
    if (targetFound) {
      action.target = targetFound.order;
    }
  }
  if (entry.outcome) {
    action.amount = entry.outcome.damageToTarget;
    action.outcome = entry.outcome;
    if (entry.outcome.attackerInstanceId && !action.attacker) {
      const a = findInGameState(gameState, entry.outcome.attackerInstanceId);
      if (a) action.attacker = a.order;
    }
    if (entry.outcome.targetInstanceId && !action.target) {
      const t = findInGameState(gameState, entry.outcome.targetInstanceId);
      if (t) action.target = t.order;
    }
  }
  return action;
}

/**
 * Hydrate full action log from server (gameState.actionLog) for display.
 * Returns array of display-ready actions in server order (chronological).
 */
export function hydrateActionLog(actionLog, gameState) {
  if (!Array.isArray(actionLog) || !gameState) return [];
  return actionLog.map(entry => hydrateActionLogEntry(entry, gameState)).filter(Boolean);
}
